// client/src/pages/seo/QrCodeMenuForRestaurantsEn.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const sections = [
  { id: "answer", label: "What is a QR code menu?" },
  { id: "create", label: "Create a QR code for your menu: how it works" },
  { id: "pdf", label: "PDF or a real digital menu?" },
  { id: "provider", label: "What should restaurants look for in a provider?" },
  { id: "bestplace", label: "How does a QR menu work with Best Place?" },
  { id: "placement", label: "Where should the QR code be placed in a restaurant?" },
  { id: "faq", label: "QR code menu FAQ" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuForRestaurantsEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title = "QR Code Menu for Restaurants | Create a Digital Menu | Best Place";
    setMeta(
      "description",
      "QR code menu for restaurants: create a digital menu, share it by QR code, and update prices, dishes and languages online at any time."
    );
    setCanonical("https://best-place.club/en/qr-code-menu-for-restaurants");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/qr-code-menu-for-restaurants",
          de: "/de/qr-code-speisekarte",
          no: "/no/qr-kode-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR Code Menu for Restaurants</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Guests scan the QR code at the table and open your current menu
                directly on their smartphone. The menu runs in the browser, with
                no app and no login.
              </p>

              <p className="seoHero__text">
                The QR code stays the same while you update prices, dishes, photos,
                availability and languages online behind it. This means table tents
                or cards do not need to be reprinted every time something changes.
              </p>

            </div>
          </div>
        </div>
      </section>

      <section className="acquaintance">
        <div className="seoComparison">
          <div className="seoComparison__col seoComparison__col--highlight">
            <h2>What you get with a QR code menu</h2>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Open the menu directly on a smartphone</li>
              <li className="text__seo-li">no app for guests</li>
              <li className="text__seo-li">update prices and dishes instantly</li>
              <li className="text__seo-li">photos, categories and descriptions</li>
              <li className="text__seo-li">multiple languages available</li>
              <li className="text__seo-li">the same QR code after updates</li>
            </ul>
          </div>

          <div className="seoComparison__col">
            <h2>What the QR code is not</h2>
            <p>
              The QR code itself is only the access point. What matters is the page
              that opens afterwards. A hard-to-read PDF behind a QR code is technically
              digital, but it is often much less convenient for guests than a mobile
              menu page.
            </p>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Contents</h2>
        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="answer" className="acquaintance">
          <h2>What is a QR code menu?</h2>
          <p>
            A QR code menu connects a printed QR code with an online restaurant
            menu. The guest scans the code with the smartphone camera, taps the link
            and sees the menu in the browser.
          </p>
          <p>
            For restaurants, the real advantage is not the QR code itself but the
            editable destination page. If a price changes, a dish sells out or a
            seasonal offer is added, you update the content online. The already
            printed QR code can stay exactly the same.
          </p>
          <p>
            That is why a QR menu should not be treated as a one-time printed
            replacement for paper, but as a continuously maintained digital access
            point to the menu.
          </p>
        </section>

        <section id="create" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Create a QR code for your menu: how it works</h2>
            <p>
              If you want to create a QR code for your menu, do not start with the
              QR image. Start with the destination page. The code is only as good as
              the menu that opens after the scan.
            </p>

            <div className="qrArticle__cardGrid">
              <div>
                <strong>1. Create the digital menu</strong>
                <p>Add categories, dishes, prices, descriptions and photos.</p>
              </div>
              <div>
                <strong>2. Check the guest view</strong>
                <p>Open the menu on a real smartphone and test it.</p>
              </div>
              <div>
                <strong>3. Generate the QR code</strong>
                <p>Connect the code to a stable menu URL.</p>
              </div>
              <div>
                <strong>4. Scan before printing</strong>
                <p>Test with several devices, lighting conditions and distances.</p>
              </div>
            </div>

            <p>
              With Best Place, you can first build the digital menu and then share
              the same menu link by QR code, website or message.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>QR code with a PDF or a real digital menu?</h2>
          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>QR code with PDF</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">quick to set up</li>
                <li className="text__seo-li">often requires zooming on small screens</li>
                <li className="text__seo-li">changes usually require a new file</li>
                <li className="text__seo-li">individual products are harder to maintain</li>
              </ul>
            </div>
            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Mobile digital menu</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">built for smartphones</li>
                <li className="text__seo-li">clear categories and readable prices</li>
                <li className="text__seo-li">products can be updated individually</li>
                <li className="text__seo-li">same QR code even when content changes</li>
              </ul>
            </div>
          </div>
          <p>
            If your menu almost never changes, a PDF may be enough. If you regularly
            manage prices, products, availability, photos or languages, a real
            digital menu page is usually more flexible.
          </p>
        </section>

        <section id="provider" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should restaurants look for in a QR menu provider?</h2>
            <p>
              Most solutions can generate a QR code. For day-to-day restaurant use,
              the other criteria are more important than the code itself.
            </p>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li"><strong>Mobile use:</strong> menu without zooming and without an app.</li>
              <li className="text__seo-li"><strong>Editing:</strong> change prices and dishes without a developer.</li>
              <li className="text__seo-li"><strong>Stable URL:</strong> the printed QR code remains usable.</li>
              <li className="text__seo-li"><strong>Languages:</strong> international guests can choose their language.</li>
              <li className="text__seo-li"><strong>Photos and categories:</strong> structure products clearly.</li>
              <li className="text__seo-li"><strong>Testing:</strong> check the guest view before rollout.</li>
            </ul>
            <p>
              If you first want to compare the main types of digital menus, read our{" "}
              <Link to="/en/digital-menu-for-restaurants">digital menu for restaurants</Link> page.
            </p>
          </div>
        </section>

        <section id="bestplace" className="acquaintance">
          <h2>How does a QR menu work with Best Place?</h2>
          <div className="qrArticle__cardGrid">
            <div>
              <strong>Create or import the menu</strong>
              <p>Manage categories, names, prices, descriptions and images.</p>
            </div>
            <div>
              <strong>Publish online</strong>
              <p>The menu gets its own publicly accessible page.</p>
            </div>
            <div>
              <strong>Use the QR code</strong>
              <p>Guests open the same up-to-date menu at the table.</p>
            </div>
            <div>
              <strong>Update later</strong>
              <p>Changes appear online without reprinting the QR code.</p>
            </div>
          </div>
        </section>

        <section id="placement" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Where should the QR code be placed in a restaurant?</h2>
            <p>
              A QR code only works well when guests see it at the right moment and
              can scan it easily. Typical placements include table tents, small cards
              on the table, the counter, windows, flyers or takeaway packaging.
            </p>
            <p>
              For print size, material, contrast and testing before production, see
              our guide:{" "}
              <Link to="/en/qr-code-menu-printing">printing a QR code for a restaurant menu</Link>.
            </p>
            <p>
              If your focus is the table tent itself, see our additional guide:{" "}
              <Link to="/en/restaurant-qr-code-table-tent">QR code table tents for restaurants</Link>.
            </p>
          </div>
        </section>

        <section id="faq" className="acquaintance">
          <h2>QR code menu FAQ</h2>
          <div className="seoFaq">
            <details>
              <summary>Do guests need an app?</summary>
              <p>
                No. A QR menu can open directly in the smartphone browser. Read more
                on the{" "}<Link to="/en/qr-code-menu-without-app">QR code menu without app</Link> page.
              </p>
            </details>
            <details>
              <summary>Does the QR code need to be reprinted after a price change?</summary>
              <p>
                Not if the code points to a stable digital menu URL. In that case,
                you only update the menu content.
              </p>
            </details>
            <details>
              <summary>Can I show food and drinks through the same QR code?</summary>
              <p>
                Yes. For most restaurants, one menu with clear categories is easier
                than several different QR codes on the same table.
              </p>
            </details>
            <details>
              <summary>Can the menu have multiple languages?</summary>
              <p>
                Yes. A digital menu can offer several language versions. The guest
                opens the same entry point and then chooses a language.
              </p>
            </details>
            <details>
              <summary>Can I test the QR menu first?</summary>
              <p>
                Yes. Test the guest view and the scan flow before printing the final
                table materials.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Related topics</h2>
          <div className="seoCompareSmall">
            <p><Link to="/en/digital-menu-for-restaurants">Digital menu for restaurants</Link></p>
            <p><Link to="/en/qr-code-menu-without-app">QR menu without an app</Link></p>
            <p><Link to="/en/qr-code-menu-printing">QR code menu printing</Link></p>
            <p><Link to="/en/manage-menu-online-for-restaurants">Manage a menu online</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
